import {ImpairedVisionData} from './webgl-visual-impairment.service';
import {Observable} from 'rxjs';

export class WebGLVisualImpairment {
    private canvasElement: HTMLCanvasElement;
    private glLoseContext?: WEBGL_lose_context;
    private gl?: WebGLRenderingContext;
    private shaderProgram: WebGLProgram;
    private vertexShader: WebGLShader;
    private fragmentShader: WebGLShader;
    private arrayBuffer: WebGLBuffer;
    private texture: WebGLTexture;

    private uImage?: WebGLUniformLocation;
    private uDuotone?: WebGLUniformLocation;
    private uDuoDark?: WebGLUniformLocation;
    private uDuoLight?: WebGLUniformLocation;
    private uYellowing?: WebGLUniformLocation;
    private uDoubleVision?: WebGLUniformLocation;
    private uProtanopia?: WebGLUniformLocation;
    private uDeuteranopia?: WebGLUniformLocation;
    private uTritanopia?: WebGLUniformLocation;
    private uAchromatopsia?: WebGLUniformLocation;
    private uTexelSize?: WebGLUniformLocation;
    private uBlurRadius?: WebGLUniformLocation;
    private uBrightness?: WebGLUniformLocation;
    private uContrast?: WebGLUniformLocation;

    private uniformLocateFailed: boolean = false;


    init$(canvasElement: HTMLCanvasElement): Observable<void> {
        return new Observable(subscriber => {
            this.canvasElement = canvasElement;
            this.gl = canvasElement.getContext("webgl", {
                // this preserves the GPU rendered image into the canvas for readback
                preserveDrawingBuffer: true,
                premultipliedAlpha: false
            }) ?? undefined;
            if (!this.gl) {
                subscriber.error(new Error("Error! WebGL not supported!"));
                return;
            }
            const onContextLost = (e: Event) => {
                e.preventDefault();
                this.destroy();
            };
            const onContextRestored = () => {
                this.initWebGL();
                subscriber.next();
                subscriber.complete();
            };
            // same func ref, so no need to clean the older listener
            this.canvasElement.addEventListener("webglcontextlost", onContextLost);
            this.canvasElement.addEventListener("webglcontextrestored", onContextRestored);
            if (this.gl.isContextLost() && !this.glLoseContext) {
                subscriber.error(new Error("Error! Not possible to restore the WebGL context"));
                return;
            }
            if (this.gl.isContextLost()) {
                this.glLoseContext!.restoreContext();
            } else {
                this.initWebGL();
                subscriber.next();
                subscriber.complete();
            }
        });
    }


    private initWebGL() {
        if (!this.gl) return;
        this.releaseResources();
        this.vertexShader = compileShader(this.gl, this.gl.VERTEX_SHADER, vertexShaderSource)!;
        this.fragmentShader = compileShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSource)!;
        if (!this.fragmentShader || !this.vertexShader) {
            console.error("Error! WebGL setup failed due to failed shader compilation!");
            return;
        }
        this.shaderProgram = this.gl.createProgram()!;
        this.gl.attachShader(this.shaderProgram, this.vertexShader);
        this.gl.attachShader(this.shaderProgram, this.fragmentShader);
        this.gl.linkProgram(this.shaderProgram);
        if (!this.gl.getProgramParameter(this.shaderProgram, this.gl.LINK_STATUS)) {
            console.error(`Error! failed to link shader programs. ${this.gl.getProgramInfoLog(this.shaderProgram)}`);
            this.destroy();
            return;
        }
        // two triangles to cover the whole viewport such that no pixel is clipped out
        const vertexPositions = [
            -1.0, 1.0, //top left
            1.0, 1.0, // top right
            -1.0, -1.0, // bottom left

            1.0, 1.0, // top right
            -1.0, -1.0, // bottom left
            1.0, -1.0 // bottom right
        ];
        this.arrayBuffer = this.gl.createBuffer()!;
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.arrayBuffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(vertexPositions), this.gl.STATIC_DRAW);
        const positionLoc = this.gl.getAttribLocation(this.shaderProgram, "a_position");
        if (positionLoc == -1) {
            console.error(`Error! couldn't locate 'a_position' in vertex shader`);
            this.destroy();
            return;
        }
        this.gl.enableVertexAttribArray(positionLoc);
        this.gl.vertexAttribPointer(positionLoc, 2, this.gl.FLOAT, false, 0, 0);

        this.texture = this.gl.createTexture()!;
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
        this.gl.pixelStorei(this.gl.UNPACK_FLIP_Y_WEBGL, true);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);

        this.uImage = this.getUniformLocation("u_image");
        this.uDuotone = this.getUniformLocation("u_duotone");
        this.uDuoDark = this.getUniformLocation("u_duo_light");
        this.uDuoLight = this.getUniformLocation("u_duo_dark");
        this.uYellowing = this.getUniformLocation("u_yellowing");
        this.uDoubleVision = this.getUniformLocation("u_double_vision");
        this.uProtanopia = this.getUniformLocation("u_protanopia");
        this.uDeuteranopia = this.getUniformLocation("u_deuteranopia");
        this.uTritanopia = this.getUniformLocation("u_tritanopia");
        this.uAchromatopsia = this.getUniformLocation("u_achromatopsia");
        this.uTexelSize = this.getUniformLocation('u_texel_size');
        this.uBlurRadius = this.getUniformLocation('u_blur_radius');
        this.uBrightness = this.getUniformLocation('u_brightness');
        this.uContrast = this.getUniformLocation('u_contrast');

        if (this.uniformLocateFailed) {
            this.destroy();
            return;
        }

        this.gl.viewport(0, 0, this.canvasElement.width, this.canvasElement.height);
        this.gl.useProgram(this.shaderProgram);
        this.gl.uniform1i(this.uImage!, 0);
        this.gl.uniform1f(this.uBrightness!, 1.0);
        this.gl.uniform1f(this.uBlurRadius!, 0.0);
        this.gl.uniform1f(this.uContrast!, 1.0);
        this.gl.uniform1i(this.uYellowing!, 0);
        this.gl.uniform1i(this.uDoubleVision!, 0);
        this.gl.uniform1i(this.uProtanopia!, 0);
        this.gl.uniform1i(this.uDeuteranopia!, 0);
        this.gl.uniform1i(this.uTritanopia!, 0);
        this.gl.uniform1i(this.uAchromatopsia!, 0);
        this.gl.uniform1i(this.uDuotone!, 0);
        this.gl.uniform3f(this.uDuoDark!, 0.0, 0.0, 0.0);
        this.gl.uniform3f(this.uDuoLight!, 1.0, 1.0, 1.0);
        this.gl.uniform2f(this.uTexelSize!,
            1 / this.canvasElement.width,
            1 / this.canvasElement.height
        );
        this.gl.texImage2D( this.gl.TEXTURE_2D, 0, this.gl.RGBA,
            this.canvasElement.width, this.canvasElement.height, 0,
            this.gl.RGBA, this.gl.UNSIGNED_BYTE, null
        );
        this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    }


    updateTexture(img: TexImageSource) {
        if (this.gl) {
            this.gl.useProgram(this.shaderProgram);
            this.gl.activeTexture(this.gl.TEXTURE0);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
            this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, 0, 0, this.gl.RGBA, this.gl.UNSIGNED_BYTE, img);
            this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
        }
    }


    applyEffect(impairmentData: ImpairedVisionData) {
        if (!this.gl) return;
        this.gl.uniform1f(this.uBrightness!, impairmentData.brightness / 100);
        this.gl.uniform1f(this.uBlurRadius!, impairmentData.blur / 2); // css_pixels / 2 for better approximation
        this.gl.uniform1f(this.uContrast!, impairmentData.contrast);
        this.gl.uniform1i(this.uYellowing!,impairmentData.yellowing ? 1 : 0);
        this.gl.uniform1i(this.uDoubleVision!,impairmentData.double ? 1 : 0);
        this.gl.uniform1i(this.uProtanopia!,impairmentData.protanopia ? 1 : 0);
        this.gl.uniform1i(this.uDeuteranopia!,impairmentData.deuteranopia ? 1 : 0);
        this.gl.uniform1i(this.uTritanopia!,impairmentData.tritanopia ? 1 : 0);
        this.gl.uniform1i(this.uAchromatopsia!,impairmentData.achromatopsia ? 1 : 0);
        this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    }


    destroy() {
        if (!this.gl) return;
        this.releaseResources();
        this.glLoseContext = this.gl.getExtension("WEBGL_lose_context") ?? undefined;
        this.glLoseContext?.loseContext();
        this.gl = undefined;
    }

    private releaseResources() {
        if (!this.gl) return;
        this.gl.deleteShader(this.vertexShader);
        this.gl.deleteShader(this.fragmentShader);
        this.gl.deleteProgram(this.shaderProgram);
        this.gl.deleteBuffer(this.arrayBuffer);
        this.gl.deleteTexture(this.texture);
        this.uniformLocateFailed = false;
    }


    private getUniformLocation(name: string) {
        if (!this.gl) return undefined;
        const loc = this.gl.getUniformLocation(this.shaderProgram, name);
        if (!loc) {
            console.error(`Error! Couldn't locate the uniform '${name}' in shader`);
            this.uniformLocateFailed = true;
            return undefined;
        }
        return loc;
    }
}



function compileShader(gl: WebGLRenderingContext, type: number, src: string) {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(`Error! shader compile failed: ${gl.getShaderInfoLog(shader)}`);
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}


const vertexShaderSource = `
attribute vec2 a_position;
varying vec2 v_uv;

void main() {
    // [-1, 1] to [0, 1] UV space
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
}
`;


const fragmentShaderSource = `
precision mediump float;

uniform sampler2D u_image;

uniform bool u_double_vision;
uniform bool u_protanopia;
uniform bool u_deuteranopia;
uniform bool u_tritanopia;
uniform bool u_achromatopsia;
uniform bool u_yellowing;
uniform bool u_duotone;

uniform vec2 u_texel_size;   // 1.0 / textureSize
uniform float u_blur_radius; // in pixels
uniform float u_brightness; // 1.0 = 100%
uniform float u_contrast;

// duotone
uniform vec3 u_duo_dark;
uniform vec3 u_duo_light;

varying vec2 v_uv;

float luminance(vec3 c) {
  return dot(c, vec3(0.299, 0.587, 0.114));
}

// Approximation of Gaussian blur to match the CSS filter
vec4 blur(vec2 uv) {
    vec2 r = u_texel_size * u_blur_radius; // use 3.5 for blur(7px)
    vec4 color = vec4(0.0);
    float total = 0.0;
    // Sample in a grid pattern with Gaussian weights
    for (float y = -4.0; y <= 4.0; y += 1.0) {
        for (float x = -4.0; x <= 4.0; x += 1.0) {
            vec2 offset = vec2(x, y) * r;
            float dist = length(vec2(x, y));
            float weight = exp(-dist * dist / 8.0); // Gaussian falloff
            color += texture2D(u_image, uv + offset) * weight;
            total += weight;
        }
    }
    return color / total;
}

mat4 protanopia = mat4(
    0.567, 0.558, 0,     0,
    0.433, 0.442, 0.242, 0,
    0,     0,     0.758, 0,
    0,     0,     0,     1
);
mat4 deuteranopia = mat4(
    0.625, 0.7,   0,   0,
    0.375, 0.3,   0.3, 0,
    0,     0,     0.7, 0,
    0,     0,     0,   1
);
mat4 tritanopia = mat4(
    0.95,  0,     0,     0,
    0.05,  0.433, 0.475, 0,
    0,     0.567, 0.525, 0,
    0,     0,     0,     1
);
mat4 achromatopsia = mat4(
    0.299, 0.299, 0.299, 0,
    0.587, 0.587, 0.587, 0,
    0.114, 0.114, 0.114, 0,
    0,     0,     0,     1
);
mat4 yellowing = mat4(
    1, -0.2, -0.1, 0,
    0,  1,    0,   0,
    0,  0,    1,   0,
    0,  0,    0,   1
);


void main() {
  vec4 color = blur(v_uv);
  color.rgb *= u_brightness;
  color.rgb = (color.rgb - 0.5) * u_contrast + 0.5;
  
  if (u_double_vision) {
    // Manually adjusting alpha and offset
    // to better match the result from SVG filter in Chrome
    
    vec2 flippedOffset = vec2(-10, 10) * u_texel_size; // 10px
    float offsetAlpha = 0.6;
    vec4 shifted = texture2D(u_image, v_uv + flippedOffset);
    
    // Get the original image (top layer) with reduced alpha
    vec4 base = color;
    base.a *= offsetAlpha;
    
    // Composite: offset on bottom, then alpha-reduced original on top
    color.rgb = base.rgb * base.a + shifted.rgb * shifted.a * (1.0 - base.a);
    color.a = base.a + shifted.a * (1.0 - base.a);
  }
  if (u_protanopia) {
    color = protanopia * color;
  }
  if (u_deuteranopia) {
    color = deuteranopia * color;
  }
  if (u_tritanopia) {
    color = tritanopia * color;
  }
  if (u_achromatopsia) {
    color = achromatopsia * color;
  }
  if (u_yellowing) {
    color = yellowing * color;
  }
  if (u_duotone) {
    float g = luminance(color.rgb);
    color.rgb = mix(u_duo_dark, u_duo_light, g);
  }

  gl_FragColor = color;
}
`;
